import { useState, useEffect, useMemo } from 'react';
import { useSelector } from '@legendapp/state/react';
import { ui$, setSelectedTagFilter } from '@/stores/ui$';
import { getTagTree, getUntaggedCounts, getSmartTagTree, renameTag, addTag } from '@/stores/notes$';
import { sharedTagPaths$, isTagShared, getSharedTagMembers, getSharedTagForPath } from '@/stores/sharedTags$';
import { cn } from '@/lib/utils';
import { ChevronRight, Hash, Edit2, Share2, Sparkles, HelpCircle } from 'lucide-react';
import Modal from './Modal';

// Stacked member avatars for shared tags
function MemberAvatars({ sharedTagId }) {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (sharedTagId) {
      getSharedTagMembers(sharedTagId).then(setMembers);
    }
  }, [sharedTagId]);

  if (members.length === 0) return null;

  const displayMembers = members.slice(0, 2);
  const remaining = members.length - 2;

  return (
    <div className="flex items-center -space-x-1.5 flex-shrink-0">
      {displayMembers.map((m) => {
        const initials = (m.username || m.email || 'U')[0].toUpperCase();
        return m.avatar_url ? (
          <img
            key={m.id}
            src={m.avatar_url}
            alt={m.username}
            className="w-4 h-4 rounded-full border border-background"
          />
        ) : (
          <div
            key={m.id}
            className="w-4 h-4 rounded-full border border-background bg-primary/20 text-primary flex items-center justify-center text-[7px] font-medium"
          >
            {initials}
          </div>
        );
      })}
      {remaining > 0 && (
        <div className="w-4 h-4 rounded-full border border-background bg-muted text-muted-foreground flex items-center justify-center text-[7px] font-medium">
          +{remaining}
        </div>
      )}
    </div>
  );
}

function TagTreeNode({ name, fullPath, count, archivedCount, children, depth = 0, selectedTag, onSelectTag, isVirtual, showArchived, onShareTag }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(name);
  const [isDragOver, setIsDragOver] = useState(false);
  const hasChildren = Object.keys(children).length > 0;
  const isSelected = selectedTag === fullPath;
  const shared = isTagShared(fullPath);
  const sharedTag = shared ? getSharedTagForPath(fullPath) : null;

  // Compute displayed count based on showArchived toggle
  // Always show single number = total of currently visible notes
  // When showArchived = true: show active + archived (all visible)
  // When showArchived = false: show only active (only active visible)
  const displayCount = useMemo(() => {
    return showArchived ? count + archivedCount : count;
  }, [count, archivedCount, showArchived]);

  // All nodes use Hash icon
  const getIcon = () => {
    return <Hash size={10} className="flex-shrink-0 ml-0.5" />;
  };

  const handleRename = () => {
    if (!renameValue.trim() || renameValue === fullPath) {
      setIsRenaming(false);
      return;
    }

    renameTag(fullPath, renameValue.trim());
    setIsRenaming(false);
  };

  // Drop handlers (for note-to-tag only)
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we can get the dragged data type
    const hasJson = e.dataTransfer.types.includes('application/json');
    if (!hasJson) {
      return;
    }

    // Set drop effect
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    console.log('[TagTree] ===== DROP EVENT FIRED =====', fullPath);
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      const jsonData = e.dataTransfer.getData('application/json');
      console.log('[TagTree] Drop - raw JSON data:', jsonData);

      if (!jsonData) {
        console.error('[TagTree] Drop error: No JSON data found');
        return;
      }

      const data = JSON.parse(jsonData);
      console.log('[TagTree] Drop received:', { data, target: fullPath, isVirtual });

      if (data.type === 'note') {
        // Note-to-tag: add tag to notes
        if (fullPath === '__untagged__') {
          console.log('[TagTree] Drop prevented: cannot drop on untagged');
          return;
        }

        console.log(`[TagTree] Adding tag "${fullPath}" to notes:`, data.noteIds);
        data.noteIds.forEach(noteId => {
          console.log(`[TagTree] Adding tag "${fullPath}" to note ${noteId}`);
          addTag(noteId, fullPath);
        });
        console.log(`[TagTree] Successfully added tag "${fullPath}" to ${data.noteIds.length} note(s)`);
      } else {
        console.warn('[TagTree] Unknown drop data type:', data.type);
      }
    } catch (err) {
      console.error('[TagTree] Drop error:', err);
    }
  };

  return (
    <div>
      {/* Container - handles drops only (note-to-tag) */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={(e) => {
          // Only handle click if not clicking on chevron or edit button
          if (e.target.closest('button')) {
            return;
          }
          onSelectTag(fullPath);
        }}
        className={cn(
          "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium transition-colors group cursor-pointer",
          isSelected
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-accent",
          isDragOver && "ring-2 ring-primary bg-primary/5"
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        title={`${displayCount} note${displayCount !== 1 ? 's' : ''}`}
      >
        {/* Chevron button */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="p-0.5 -ml-0.5 rounded hover:bg-accent"
          >
            <ChevronRight
              size={12}
              className={cn(
                "transition-transform flex-shrink-0",
                isExpanded && "rotate-90"
              )}
            />
          </button>
        ) : (
          <div className="w-[12px] flex-shrink-0" />
        )}

        {/* Main content area */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {getIcon()}
          <span className="flex-1 truncate text-left">{name}</span>
        </div>

        {/* Edit & Share icons - show on hover */}
        {isHovering && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsRenaming(true);
                setRenameValue(fullPath);
              }}
              className="p-0.5 rounded hover:bg-accent opacity-0 group-hover:opacity-100 transition-opacity"
              title="Rename tag"
            >
              <Edit2 size={10} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShareTag?.(fullPath);
              }}
              className="p-0.5 rounded hover:bg-accent opacity-0 group-hover:opacity-100 transition-opacity"
              title="Share tag"
            >
              <Share2 size={10} />
            </button>
          </>
        )}

        {/* Member avatars for shared tags */}
        {shared && sharedTag && !isHovering && (
          <MemberAvatars sharedTagId={sharedTag.id} />
        )}

        {/* Count badge */}
        {displayCount > 0 && (
          <span
            className={cn(
              "text-[10px] px-1.5 py-0.5 rounded-full font-mono flex-shrink-0 whitespace-nowrap h-4 flex items-center leading-none",
              isSelected
                ? "bg-primary/20 text-primary"
                : "bg-muted text-muted-foreground/60 group-hover:bg-muted/80"
            )}
          >
            {displayCount}
          </span>
        )}
      </div>

      {/* Rename modal */}
      <Modal isOpen={isRenaming} onClose={() => setIsRenaming(false)} onOverlayClick={() => setIsRenaming(false)}>
        <div className="bg-popover border border-border rounded-lg shadow-lg p-4 max-w-sm w-full mx-4">
            <h3 className="text-sm font-medium mb-3">Rename Tag</h3>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRename();
                } else if (e.key === 'Escape') {
                  setIsRenaming(false);
                }
              }}
              className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Full tag path (e.g., project/web/site)"
              autoFocus
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={() => setIsRenaming(false)}
                className="px-3 py-1.5 text-sm rounded-md hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRename}
                className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Rename
              </button>
            </div>
          </div>
      </Modal>

      {/* Render children */}
      {hasChildren && isExpanded && (
        <div className="mt-0.5">
          {Object.values(children).sort((a, b) => a.name.localeCompare(b.name)).map((child) => (
            <TagTreeNode
              key={child.fullPath}
              name={child.name}
              fullPath={child.fullPath}
              count={child.count}
              archivedCount={child.archivedCount}
              children={child.children}
              depth={depth + 1}
              selectedTag={selectedTag}
              onSelectTag={onSelectTag}
              isVirtual={child.isVirtual}
              showArchived={showArchived}
              onShareTag={onShareTag}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TagTree({ onShareTag, onNavigate, onOpenSettings }) {
  const selectedTagFilter = useSelector(() => ui$.selectedTagFilter.get());
  const showArchived = useSelector(() => ui$.showArchived.get());
  const smartTagsEnabled = useSelector(() => ui$.smartTagsEnabled.get());
  const tagTree = useSelector(() => getTagTree()); // Make reactive
  const untaggedCounts = useSelector(() => getUntaggedCounts()); // Make reactive
  const smartTags = useSelector(() => getSmartTagTree()); // Smart tags reactive
  // Subscribe to sharedTagPaths$ so avatars re-render when sharing changes
  useSelector(() => sharedTagPaths$.get());
  const hasAnyTags = Object.keys(tagTree).length > 0;

  // Compute displayed untagged counts based on showArchived
  // Always show single number = total of currently visible notes
  // When showArchived = true: show active + archived (all visible)
  // When showArchived = false: show only active (only active visible)
  const displayedUntaggedCount = useMemo(() => {
    return showArchived ? untaggedCounts.active + untaggedCounts.archived : untaggedCounts.active;
  }, [untaggedCounts.active, untaggedCounts.archived, showArchived]);

  return (
    <div>
      {/* Header */}
      <div className="px-2 py-1.5 mb-1">
        <h3 className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider">
          Library
        </h3>
      </div>

      <div className="space-y-0.5">
        {/* Untagged special item - always visible */}
        <button
          onClick={() => {
            setSelectedTagFilter(selectedTagFilter === '__untagged__' ? null : '__untagged__');
            onNavigate?.();
          }}
          className={cn(
            "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium transition-colors group",
            selectedTagFilter === '__untagged__'
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-accent"
          )}
          title={`${displayedUntaggedCount} untagged note${displayedUntaggedCount !== 1 ? 's' : ''}`}
        >
          <div className="w-[12px] flex-shrink-0" />
          <Hash size={10} className="flex-shrink-0 ml-0.5" />
          <span className="flex-1 truncate text-left">Untagged</span>
          {displayedUntaggedCount > 0 && (
            <span
              className={cn(
                "text-[10px] px-1.5 py-0.5 rounded-full font-mono flex-shrink-0 whitespace-nowrap h-4 flex items-center leading-none",
                selectedTagFilter === '__untagged__'
                  ? "bg-primary/20 text-primary"
                  : "bg-muted text-muted-foreground/60 group-hover:bg-muted/80"
              )}
            >
              {displayedUntaggedCount}
            </span>
          )}
        </button>

        {/* Smart Tags section */}
        {smartTagsEnabled && smartTags.length > 0 && (
          <>
            <div className="h-px bg-border/50 my-1" />
            <div className="px-2 py-1 mb-0.5 flex items-center justify-between">
              <h4 className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1">
                <Sparkles size={9} />
                Smart Tags
              </h4>
              <button
                onClick={() => onOpenSettings?.('features')}
                className="text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                title="AI auto-classifies your notes. Click to manage in Settings."
              >
                <HelpCircle size={10} />
              </button>
            </div>
            {smartTags.map(({ tag, label, count }) => (
              <button
                key={tag}
                onClick={() => {
                  setSelectedTagFilter(selectedTagFilter === tag ? null : tag);
                  onNavigate?.();
                }}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium transition-colors group",
                  selectedTagFilter === tag
                    ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}
              >
                <div className="w-[12px] flex-shrink-0" />
                <Sparkles size={10} className="flex-shrink-0 ml-0.5 text-amber-500" />
                <span className="flex-1 truncate text-left">{label}</span>
                {count > 0 && (
                  <span
                    className={cn(
                      "text-[10px] px-1.5 py-0.5 rounded-full font-mono flex-shrink-0 whitespace-nowrap h-4 flex items-center leading-none",
                      selectedTagFilter === tag
                        ? "bg-amber-500/20 text-amber-700 dark:text-amber-300"
                        : "bg-muted text-muted-foreground/60 group-hover:bg-muted/80"
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            ))}
          </>
        )}

        {/* Divider between special sections and regular tags */}
        {hasAnyTags && (
          <div className="h-px bg-border/50 my-1" />
        )}

        {/* Tag tree */}
        {!hasAnyTags ? (
          <div className="px-2 py-4 text-center">
            <p className="text-xs text-muted-foreground/60">No tags yet</p>
          </div>
        ) : (
          Object.values(tagTree).sort((a, b) => a.name.localeCompare(b.name)).map((node) => (
            <TagTreeNode
              key={node.fullPath}
              name={node.name}
              fullPath={node.fullPath}
              count={node.count}
              archivedCount={node.archivedCount}
              children={node.children}
              depth={0}
              selectedTag={selectedTagFilter}
              onSelectTag={(tag) => {
                setSelectedTagFilter(tag);
                onNavigate?.();
              }}
              isVirtual={node.isVirtual}
              showArchived={showArchived}
              onShareTag={onShareTag}
            />
          ))
        )}
      </div>
    </div>
  );
}
